import { useState, type FormEvent } from 'react';
import { Mail, User } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useCreateUser } from '@/hooks/useUsers';
import { ROLES, ROLE_LABELS, type Role } from '@/types';

type Props = { open: boolean; onClose: () => void };

export function InviteUserModal({ open, onClose }: Props) {
  const create = useCreateUser();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [department, setDepartment] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail(''); setDisplayName(''); setRole('staff');
    setDepartment(''); setSendInvite(true); setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        email: email.trim(),
        displayName: displayName.trim(),
        role,
        department: department.trim() || undefined,
        sendInvite,
      });
      toast.success(`Invited ${displayName}`, {
        description: sendInvite ? 'A welcome email has been sent.' : 'Account created without invite email.',
      });
      reset();
      onClose();
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Failed to create user.';
      setError(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      kicker="New User"
      title="Invite a team member."
      description="Provision a new portal account. The user will receive an email with a link to set their password."
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="invite-form" loading={create.isPending}>
            Send Invite
          </Button>
        </>
      }
    >
      <form id="invite-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <Input
          label="Full name"
          name="displayName"
          required
          placeholder="Jane Doe"
          leftIcon={<User size={15} />}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <Input
          label="Email"
          name="email"
          type="email"
          required
          placeholder="jane@talbert.app"
          leftIcon={<Mail size={15} />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          hint="Talbert email recommended."
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            name="role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
          />

          <Input
            label="Department"
            name="department"
            placeholder="Optional"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
        </div>

        <div className="pt-2 border-t border-border">
          <Switch
            checked={sendInvite}
            onChange={setSendInvite}
            label="Send invite email"
            description="Sends a password-setup link via SES. Disable only if you intend to share credentials manually."
          />
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
